/**
 * A tile in the effects panel, showing the effect it would apply.
 *
 * Drawn with the real thing: the object's own font or shape, through the same
 * pass list and the same canvas painter the design itself uses. A tile drawn
 * any other way - a stock picture, CSS text-shadow - would show a glow that is
 * not the glow the customer is about to get.
 */

import { useEffect, useRef, useState } from 'react';
import type { Colour, Effect, ShapeLayer, TextLayer } from '../model/types';
import { isGradient } from '../model/types';
import { layoutFor } from '../text/layout';
import { placeText } from '../text/place';
import { onFontsChanged } from '../text/fonts';
import { plan } from '../effects/plan';
import { paintPasses, pathFor } from '../canvas/paint';
import { shapePath } from '../shapes/library';

/** The tile's drawing area, in CSS pixels. */
const SIZE = 72;

interface Props {
  layer: TextLayer | ShapeLayer;
  label: string;
  /** What to show. null shows the object as it is. */
  effect: Effect | null;
  /** For the curve tile: bend the sample. */
  curve?: number;
  /** For the line tile: lay the sample along a gentle wave. */
  wave?: boolean;
  /** For a preset: its own font and colour. */
  font?: { family: string; weight: number };
  fill?: Colour;
  /** What the tile says, for text. */
  sample?: string;
  selected: boolean;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
}

export function EffectTile( props: Props ) {
  const { label, selected, disabled, title, onClick } = props;
  const canvas = useRef<HTMLCanvasElement>( null );
  const [ fonts, setFonts ] = useState( 0 );

  // A preset's font may still be on its way; draw again when it lands.
  useEffect( () => onFontsChanged( () => setFonts( ( n ) => n + 1 ) ), [] );

  useEffect( () => {
    const element = canvas.current;

    if ( element ) draw( element, props );
    // Everything the drawing depends on is in props; `fonts` makes it retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ props.layer, props.effect, props.curve, props.wave, props.font, props.fill, props.sample, fonts ] );

  return (
    <button
      type="button"
      className={ `tb-tile${ selected ? ' is-on' : '' }` }
      aria-pressed={ selected }
      disabled={ disabled }
      title={ title ?? label }
      onClick={ onClick }
    >
      <canvas ref={ canvas } className="tb-tile__art" width={ SIZE } height={ SIZE } aria-hidden="true" />
      <span className="tb-tile__label">{ label }</span>
    </button>
  );
}

function draw( element: HTMLCanvasElement, props: Props ): void {
  const ratio = Math.min( 3, window.devicePixelRatio || 1 );

  element.width = SIZE * ratio;
  element.height = SIZE * ratio;

  const ctx = element.getContext( '2d' );

  if ( ! ctx ) return;

  ctx.setTransform( ratio, 0, 0, ratio, 0, 0 );
  ctx.clearRect( 0, 0, SIZE, SIZE );

  const ink = tileInk( props.fill ?? ( firstOf( props.layer.fill ) ) );

  if ( props.layer.type === 'text' ) {
    const sample: TextLayer = {
      ...props.layer,
      text: props.sample ?? 'Ag',
      font: {
        ...props.layer.font,
        ...( props.font ? { family: props.font.family, weight: props.font.weight, style: 'normal' as const } : {} ),
        size: 28,
      },
      w: 0,
      h: 0,
      align: 'left',
      direction: 'ltr',
      lineHeight: 1,
      letterSpacing: 0,
      underline: false,
      strike: false,
      fill: ink,
      stroke: null,
      effect: props.effect,
      curve: props.curve ?? 0,
      path: null,
    };

    const laid = layoutFor( sample );

    if ( ! laid ) return;

    // The wave is an S across a box a little wider than the word, so the
    // letters visibly ride it rather than sit on a gentle slope.
    const width = laid.paragraph.width / laid.unitsPerMm;
    const placed = props.wave
      ? placeText( {
          ...sample,
          w: width * 1.15,
          h: width * 0.35,
          path: {
            nodes: [
              { x: 0, y: 0.8, out: { x: 0.25, y: -0.2 } },
              { x: 1, y: 0.2, in: { x: 0.75, y: 1.2 } },
            ],
            closed: false,
          },
        }, laid )
      : placeText( sample, laid );
    const box = placed.extent;

    // The sample fills a little over half the tile, leaving room for the
    // effect to show round it.
    const scale = Math.min( ( SIZE * 0.6 ) / Math.max( 0.1, box.w ), ( SIZE * 0.5 ) / Math.max( 0.1, box.h ) );

    ctx.translate( SIZE / 2 - ( box.x + box.w / 2 ) * scale, SIZE / 2 - ( box.y + box.h / 2 ) * scale );
    ctx.scale( scale, scale );

    const passes = plan( sample, { fill: ink, stroke: null, width: 0 }, box );

    paintPasses( ctx, passes, placed.glyphs.map( ( g ) => ( { path: pathFor( g.d ), m: g.m } ) ), { letters: true } );

    return;
  }

  // A shape: the shape itself, at the tile's proportions.
  const aspect = props.layer.w / Math.max( 0.1, props.layer.h );
  const w = aspect >= 1 ? 30 : 30 * aspect;
  const h = aspect >= 1 ? 30 / aspect : 30;
  const sample: ShapeLayer = { ...props.layer, w, h, rotation: 0, fill: ink, effect: props.effect, shadow: null };
  const scale = ( SIZE * 0.5 ) / 30;

  ctx.translate( SIZE / 2 - ( w / 2 ) * scale, SIZE / 2 - ( h / 2 ) * scale );
  ctx.scale( scale, scale );

  const passes = plan(
    sample,
    { fill: ink, stroke: sample.stroke ? sample.stroke.colour : null, width: sample.stroke ? sample.stroke.width : 0 },
    { x: 0, y: 0, w, h }
  );

  paintPasses( ctx, passes, [ { path: pathFor( shapePath( sample ) ), m: [ 1, 0, 0, 1, 0, 0 ] } ] );
}

function firstOf( fill: ShapeLayer[ 'fill' ] ): Colour {
  return isGradient( fill ) ? fill.stops[ 0 ]?.colour ?? { space: 'cmyk', value: [ 0, 0, 0, 1 ] } : fill;
}

/**
 * The colour a tile draws in: the object's own, unless it is so pale it would
 * vanish on the tile, in which case a dark grey stands in for it.
 */
function tileInk( colour: Colour ): Colour {
  if ( colour.space === 'rgb' ) return colour;

  const ink = colour.space === 'spot' ? colour.fallback : colour.value;
  const total = ink.reduce( ( sum, v ) => sum + v, 0 );

  return total < 0.35 ? { space: 'cmyk', value: [ 0, 0, 0, 0.8 ] } : colour;
}
